import React from 'react';

import { forwardRef, Text as ChakraText } from '@chakra-ui/react';

import { TextSize22, TextV22Props } from '../../types';

const DEFAULT_SIZE: TextSize22 = 'body-500';

/**
 * DLS 2.2 Text — the numeric `body-*` scale.
 *
 * `body-*` is copy and nothing else. Headings are `Heading`'s `title-*` and display type is
 * `Display`'s `display-*`; the two steps 2.1 called `body-xl` and `body-xxl` were heading
 * metrics wearing a body name, which is the mistake the numeric scale removes. `body-xl` becomes
 * `<Display size="display-400">`; `body-xxl` has no counterpart.
 *
 * Weight is a prop, not a step — `fontWeight="medium"` / `"semibold"`. Figma calls 600 "Bold",
 * so its `-bold` styles mean semibold; 700 is a weight Inter Republic cannot render.
 *
 * `-compact` is part of the size name rather than a modifier, because it is a step in the scale
 * with its own token and only `body-400` and `body-500` have one. Asking for a compact step that
 * does not exist is a compile error rather than a silent no-op.
 */
export const TextV22 = forwardRef<TextV22Props, 'p'>((props, ref) => {
  const { size = DEFAULT_SIZE, tabular = false, sx, ...rest } = props;

  return (
    <ChakraText
      ref={ref}
      size={size}
      // Chakra has no style prop for `font-variant-numeric`. Not `__css`: Chakra's own `Text`
      // sets `__css={styles}` after spreading its rest props, so one passed in from here is
      // dropped. Merging into `sx` puts it on a layer that survives, and spreading the call
      // site's own `sx` afterwards keeps it the winner.
      sx={tabular ? { fontVariantNumeric: 'tabular-nums', ...sx } : sx}
      {...rest}
    />
  );
});

TextV22.displayName = 'Text';
